import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Paper,
  Chip,
  Stack,
} from "@mui/material";
import {
  Inventory2,
  LocalShipping,
  Analytics,
  AutoAwesome,
  TrendingUp,
  ArrowOutward,
  EastRounded,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import SettingsEthernetRoundedIcon from "@mui/icons-material/SettingsEthernetRounded";
const MotionBox = motion(Box);
const MotionPaper = motion(Paper);

const floatingCards = [
  {
    icon: <Inventory2 />,
    title: "Inventory",
    position: {
      top: 20,
      left: { xs: 5, sm: 40, md: 55 },
    },
  },
  {
    icon: <LocalShipping />,
    title: "Logistics",
    position: {
      top: { xs: 90, md: 110 },
      right: { xs: 0, sm: 35, md: 45 },
    },
  },
  {
    icon: <Analytics />,
    title: "Analytics",
    position: {
      bottom: { xs: 55, md: 75 },
      right: { xs: 10, sm: 70, md: 95 },
    },
  },
  {
    icon: <TrendingUp />,
    title: "Planning",
    position: {
      bottom: { xs: 25, md: 40 },
      left: { xs: 15, sm: 65, md: 90 },
    },
  },
];

const highlightChips = [
  "Real-Time Visibility",
  "Inventory Optimization",
  "Procurement",
  "Enterprise Integration",
];

const keyCapabilities = [
  "Real-Time Supply Chain Visibility",
  "Inventory Optimization",
  "Procurement & Vendor Management",
  "Warehouse & Logistics Management",
  "Demand Forecasting & Planning",
  "Integrated Supply Chain Operations",
  "Intelligent Automation",
  "Analytics & Performance Monitoring",
];

const businessBenefits = [
  "Improved operational efficiency",
  "Reduced procurement and inventory costs",
  "Faster order fulfillment",
  "Better supplier collaboration",
  "Enhanced decision-making with real-time insights",
  "Increased supply chain agility and resilience",
  "Reduced manual intervention through automation",
  "Improved customer satisfaction and service levels",
];

const sectionCardStyle = {
  p: 2.5,
  borderRadius: "24px",
  backdropFilter: "blur(14px)",
  height: "100%",
};

function InfoListCard({ title, items, type = "spark" }) {
  return (
    <Box sx={{ py: 1 }}>
      <Typography
        sx={{
          fontWeight: 800,
          color: "#071525",
          mb: 2.5,
          fontSize: { xs: "1rem", md: "1.15rem" },
        }}
      >
        {title}
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)",
          },
          gap: { xs: 1.5, md: 2 },
        }}
      >
        {items.map((item) => (
          <MotionBox
            key={item}
            whileHover={{ y: -3 }}
            transition={{ duration: 0.25 }}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1.2,
              p: 0.5,
            }}
          >
            {/* ICON STYLE */}
            {type === "spark" ? (
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  minWidth: 24,
                  borderRadius: "8px",
                  background:
                    "linear-gradient(135deg, rgba(0,200,83,0.10), rgba(8,45,99,0.10))",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mt: "2px",
                }}
              >
                <AutoAwesome
                  sx={{
                    fontSize: 15,
                    color: "#00a85a",
                  }}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  minWidth: 24,
                  borderRadius: "8px",
                  background:
                    "linear-gradient(135deg, rgba(0,200,83,0.10), rgba(8,45,99,0.10))",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mt: "2px",
                }}
              >
                <EastRounded
                  sx={{
                    fontSize: 18,
                    color: "#00a85a",
                    mt: "2px",
                    minWidth: 18,
                  }}
                />
              </Box>
            )}

            {/* TEXT */}
            <Typography
              sx={{
                color: "#5d6b7c",
                fontSize: { xs: "0.84rem", md: "0.92rem" },
                fontWeight: 500,
                lineHeight: 1.6,
              }}
            >
              {item}
            </Typography>
          </MotionBox>
        ))}
      </Box>
    </Box>
  );
}

function SupplyChainContent() {
  return (
    <>
      <Chip
        icon={<AutoAwesome />}
        label="Smart Supply Chain Solutions"
        sx={{
          mb: 2.5,
          height: 38,
          px: 1,
          fontWeight: 700,
          color: "#067a46",
          background: "rgba(0,200,83,0.10)",
          border: "1px solid rgba(0,200,83,0.18)",
        }}
      />

      <Typography
        variant="h3"
        sx={{
          fontWeight: 700,
          color: "#071525",
          fontSize: { xs: "1rem", md: "1.6rem" },
          lineHeight: 1.15,
          mb: 2.5,
        }}
      >
        Enhance visibility, optimize inventory & streamline procurement
        operations.
      </Typography>

      <Typography
        sx={{
          color: "#5d6b7c",
          lineHeight: 1.9,
          fontSize: { xs: "0.75rem", md: "1rem" },
          textAlign: "justify",
        }}
      >
        Transform your supply chain into a connected, intelligent, and
        data-driven ecosystem with our Smart Supply Chain Solutions. We help
        organizations improve operational visibility, optimize inventory levels,
        streamline procurement processes, and enhance overall supply chain
        efficiency through advanced digital technologies and enterprise
        integration.
      </Typography>

      <Stack direction="row" spacing={1.2} flexWrap="wrap" sx={{ mt: 3.5 }}>
        {highlightChips.map((item) => (
          <Chip
            key={item}
            label={item}
            sx={{
              mb: 1,
              fontWeight: 650,
              color: "#082d63",
              background: "#eef6ff",
              border: "1px solid rgba(8,45,99,0.08)",
            }}
          />
        ))}
      </Stack>
    </>
  );
}

function SupplyChainVisual() {
  return (
    <Box
      sx={{
        position: "relative",
        minHeight: { xs: 360, md: 470 },
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <MotionBox
        animate={{ rotate: 360 }}
        transition={{ duration: 28, repeat: Infinity, ease: "linear" }}
        sx={{
          position: "absolute",
          width: { xs: 280, md: 380 },
          height: { xs: 280, md: 380 },
          borderRadius: "50%",
          border: "1px dashed rgba(8,45,99,0.18)",
        }}
      />

      <MotionBox
        animate={{ scale: [1, 1.08, 1] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        sx={{
          position: "absolute",
          width: { xs: 210, md: 280 },
          height: { xs: 210, md: 280 },
          borderRadius: "50%",
          background:
            "linear-gradient(135deg, rgba(0,200,83,0.12), rgba(8,45,99,0.08))",
        }}
      />

      <MotionBox
        animate={{ y: [0, -10, 0] }}
        transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
        sx={{
          width: { xs: 145, md: 175 },
          height: { xs: 145, md: 175 },
          borderRadius: "32px",
          background: `
            linear-gradient(
              135deg,
              #021B3A 0%,
              #082D63 35%,
              #005BFF 68%,
              #00C853 100%
            )
          `,
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 28px 70px rgba(8,45,99,0.28)",
          zIndex: 3,
        }}
      >
        <SettingsEthernetRoundedIcon sx={{ fontSize: { xs: 58, md: 76 } }} />
      </MotionBox>

      {floatingCards.map((item, index) => (
        <MotionBox
          key={item.title}
          animate={{ y: [0, -12, 0] }}
          transition={{
            duration: 3.2 + index * 0.4,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          sx={{
            position: "absolute",
            ...item.position,
            zIndex: 4,
            minWidth: { xs: 105, md: 130 },
            p: { xs: 1.5, md: 2 },
            borderRadius: "22px",
            background: "rgba(255,255,255,0.88)",
            backdropFilter: "blur(14px)",
            border: "1px solid rgba(8,45,99,0.08)",
            boxShadow: "0 20px 55px rgba(8,45,99,0.14)",
            textAlign: "center",
          }}
        >
          <Box sx={{ color: "#0755c9", mb: 0.6 }}>{item.icon}</Box>

          <Typography
            sx={{
              color: "#082d63",
              fontWeight: 800,
              fontSize: { xs: "0.82rem", md: "0.95rem" },
            }}
          >
            {item.title}
          </Typography>
        </MotionBox>
      ))}

      <Box
        sx={{
          position: "absolute",
          width: "72%",
          height: 2,
          background:
            "linear-gradient(90deg, transparent, rgba(0,200,83,0.35), transparent)",
          top: "50%",
          left: "14%",
          zIndex: 1,
        }}
      />

      <Box
        sx={{
          position: "absolute",
          width: 2,
          height: "70%",
          background:
            "linear-gradient(180deg, transparent, rgba(8,45,99,0.25), transparent)",
          top: "15%",
          left: "50%",
          zIndex: 1,
        }}
      />
    </Box>
  );
}

export default function FirstDataSection() {
  return (
    <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2, py: 3 }}>
      <MotionPaper
        initial={{ opacity: 0, y: 35 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        elevation={0}
        sx={{
          position: "relative",
          overflow: "hidden",
          background: "transparent",
          boxShadow: "none",
          border: "none",
        }}
      >
        <Grid container spacing={{ xs: 5, md: 8 }} alignItems="center">
          <Grid item size={{ xs: 12, md: 6 }}>
            <SupplyChainContent />
          </Grid>

          <Grid item size={{ xs: 12, md: 6 }}>
            <SupplyChainVisual />
          </Grid>
        </Grid>

        <InfoListCard
          title="Key Capabilities"
          items={keyCapabilities}
          type="spark"
        />

        <InfoListCard
          title="Business Benefits"
          items={businessBenefits}
          type="square"
        />
      </MotionPaper>
    </Container>
  );
}
